import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import { HelperTooltip } from "../../sidebar-menu/components/helper-tooltip";
import { getIcon } from "../use-menu-links/icons";
import { jsx as _jsx } from "react/jsx-runtime";
export var createTooltip = function createTooltip(tooltip) {
  if (!tooltip) return null;
  if (typeof tooltip === 'string') return /*#__PURE__*/_jsx(HelperTooltip, {
    title: tooltip,
    description: ""
  });
  return /*#__PURE__*/_jsx(HelperTooltip, {
    title: tooltip.title,
    description: tooltip.description
  });
};

/**
 * Excludes specified links from the menu structure based on their codes.
 *
 * @param links - An array of TBaseMenuLink objects representing the menu structure.
 * @param codesToExclude - An array of string codes identifying links to be excluded.
 * @returns A new array of TBaseMenuLink objects with specified links removed.
 *
 * This function recursively filters out links whose codes are present in the
 * codesToExclude array. It also processes nested children, ensuring that
 * exclusions are applied throughout the entire menu structure.
 *
 * @example
 * const menuLinks = [
 *   { code: 'HOME', label: 'Home' },
 *   { code: 'PRODUCTS', label: 'Products', children: [
 *     { code: 'PRODUCT_A', label: 'Product A' },
 *     { code: 'PRODUCT_B', label: 'Product B' }
 *   ]}
 * ];
 * const filteredLinks = excludeLinks(menuLinks, ['PRODUCT_A']);
 * // Result: HOME and PRODUCTS (with only PRODUCT_B as child) remain
 */
var _excludeLinks = function excludeLinks(links, codesToExclude) {
  return links.map(function (link) {
    if (codesToExclude.includes(link.code)) {
      return null;
    }
    var newLink = _objectSpread({}, link);
    if (newLink.children && newLink.children.length > 0) {
      newLink.children = _excludeLinks(newLink.children, codesToExclude).filter(Boolean);
    }
    return newLink;
  }).filter(Boolean);
};

/**
 * Adds additional data to the menu links.
 *
 * This function takes an array of base menu links and an object containing additional data,
 * and returns a new array of menu links with the additional data applied.
 *
 * @param links - An array of TBaseMenuLink objects representing the menu structure.
 * @param data - An object containing additional data to be applied to the links.
 * @returns A new array of TBaseMenuLink objects with the additional data applied.
 *
 * The function recursively traverses the menu structure, updating labels for links
 * that have corresponding entries in the additional data object. It preserves the
 * original structure and properties of links that don't have additional data.
 */
export { _excludeLinks as excludeLinks };
export var addAdditionalData = function addAdditionalData(links, data) {
  var _updateLinkWithAdditionalData = function updateLinkWithAdditionalData(link, additionalData) {
    var linkData = additionalData[link.code];
    var updatedLink = linkData ? _objectSpread(_objectSpread({}, link), {}, {
      label: linkData.label ? linkData.label : link.label,
      url: !link.url && linkData.url ? linkData.url : link.url
    }) : _objectSpread({}, link);
    if (updatedLink.children && updatedLink.children.length > 0) {
      return _objectSpread(_objectSpread({}, updatedLink), {}, {
        children: updatedLink.children.map(function (child) {
          return _updateLinkWithAdditionalData(child, additionalData);
        })
      });
    }
    return updatedLink;
  };
  return links.map(function (link) {
    return _updateLinkWithAdditionalData(link, data);
  });
};

/**
 * Creates a menu link object from the given parameters.
 *
 * @param params - The base menu link object containing various properties.
 * @param t - Optional translation function for internationalizing labels.
 * @param orgSlug - Optional organization slug to replace in URLs.
 * @param orgSlugPatternToReplace - Optional pattern to replace with orgSlug in URLs. Defaults to '[orgSlug]'.
 * @returns An IMenuLink object with processed properties.
 */
function createMenuLink(params, orgSlug, orgSlugPatternToReplace, t) {
  var type = params.type,
    code = params.code,
    label = params.label,
    icon = params.icon,
    url = params.url,
    tooltip = params.tooltip,
    _params$decoratedIcon = params.decoratedIcon,
    decoratedIcon = _params$decoratedIcon === void 0 ? false : _params$decoratedIcon,
    _params$arrowDefault = params.arrowDefault,
    arrowDefault = _params$arrowDefault === void 0 ? false : _params$arrowDefault,
    _params$isLocked = params.isLocked,
    isLocked = _params$isLocked === void 0 ? false : _params$isLocked,
    tourSelector = params.tourSelector,
    exact = params.exact,
    _params$children = params.children,
    children = _params$children === void 0 ? [] : _params$children;
  return _objectSpread(_objectSpread({}, params), {}, {
    type: type,
    code: code,
    label:
    // Check if is uppercase to apply translation using the label as KEY
    label === label.toUpperCase() && t ? t(label) : label,
    icon: getIcon(icon),
    url: url && orgSlug ? url.replace(orgSlugPatternToReplace, orgSlug) : '',
    tooltip: createTooltip(tooltip),
    decoratedIcon: decoratedIcon,
    arrowDefault: arrowDefault,
    isLocked: isLocked,
    tourSelector: tourSelector,
    exact: exact,
    children: children.map(function (child) {
      return createMenuLink(child, orgSlug, orgSlugPatternToReplace, t);
    })
  });
}
export var createMenuLinks = function createMenuLinks(_ref) {
  var links = _ref.links,
    orgSlug = _ref.orgSlug,
    orgSlugPatternToReplace = _ref.orgSlugPatternToReplace,
    t = _ref.t;
  return links.map(function (link) {
    return createMenuLink(link, orgSlug, orgSlugPatternToReplace, t);
  });
};

/**
 * Recursively extracts all codes from the FULL_SUITE_COLLABORATOR_LINKS array.
 *
 * @param links - The array of TBaseMenuLink objects to extract codes from.
 * @returns A flat array of all codes found in the links and their children.
 */
export var getAllCodes = function getAllCodes(links) {
  var codes = [];
  var _extractCodes = function extractCodes(link) {
    if (link.code) {
      codes.push(link.code);
    }
    if (link.children) {
      link.children.forEach(_extractCodes);
    }
  };
  links.forEach(_extractCodes);
  return codes;
};

/**
 * Returns the difference between two string arrays.
 *
 * @param array1 - The first array of strings.
 * @param array2 - The second array of strings to subtract from the first.
 * @returns An array of strings that are in array1 but not in array2.
 */
export var arrayDifference = function arrayDifference(array1, array2) {
  var set2 = new Set(array2);
  return array1.filter(function (item) {
    return !set2.has(item);
  });
};
var LEGACY_HANA_AI_MODULE = {
  type: 'url',
  code: 'HANA_AI',
  label: 'Hana AI',
  url: '/org/[orgSlug]/panel/hana-ai/',
  icon: 'logos/Hana',
  decoratedIcon: true,
  tooltip: {
    title: 'Hana AI',
    description: 'Analiza la data de tu compañía con Inteligencia Artificial.'
  }
};
export function transformHanaModule(links) {
  return links.map(function (link) {
    if (link.code === 'PRODUCTS_MODULE' && link.children) {
      return _objectSpread(_objectSpread({}, link), {}, {
        children: link.children.map(function (child) {
          return child.code === 'HANA_AI' ? LEGACY_HANA_AI_MODULE : child;
        })
      });
    }
    return link;
  });
}